import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import Mobile from './mobile';

// Auto-redirect mobile devices to /m (once, preserving query/hash)
const isMobileDevice = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const onMobilePath = window.location.pathname.startsWith('/m');
// Public token links (joining form, candidate portal) must open the desktop
// App, which renders them before any login. The mobile shell has no route
// for them and shows the login screen instead.
const isPublicTokenPath = /^\/(onboarding-form|candidate)\//.test(window.location.pathname);
// The same goes for the emailed / shared public links that the desktop App renders without a login:
// exit interview, self-apply, employee intake and guest chat — whether they come as a path (/exit-interview)
// or as a hash (/#exit-interview?token=…). On a phone these used to be bounced to /m, which showed the
// logged-in mobile home screen (or the login screen) instead of the form.
const PUBLIC_LINK = /^(apply|intake|exit-interview|guest-chat)(\?|$)/;
const isPublicLinkPath = PUBLIC_LINK.test(window.location.pathname.replace(/^\/+|\/+$/g, ''));
const isPublicLinkHash = PUBLIC_LINK.test(window.location.hash.replace(/^#\/?/, ''));
const isPublicLink = isPublicLinkPath || isPublicLinkHash;
if (isMobileDevice && !onMobilePath && !isPublicTokenPath && !isPublicLink) {
  window.location.replace('/m' + window.location.search + window.location.hash);
}
// Already landed on /m with a public link (e.g. an earlier redirect was saved or shared): hand it back to the desktop App.
if (onMobilePath && isPublicLinkHash) {
  window.location.replace('/' + window.location.search + window.location.hash);
}

// Route by URL: /m or /m/* → Mobile shell; /guide → static file; everything else → Desktop App
const isMobileRoute = onMobilePath;
const isGuideRoute  = window.location.pathname.startsWith('/guide');

// /guide is served as a static HTML file from public/guide/index.html
// Just let the browser load it; no React needed.
if (!isGuideRoute) {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      {isMobileRoute ? <Mobile /> : <App />}
    </React.StrictMode>
  );
}

// PWA: register service worker (only when served over HTTPS or localhost).
// Scoped to /m/ — sw.js's own fetch handler is written only for the mobile
// shell (cache-first on /static/, navigate-fallback on /m), but a bare
// register('/sw.js') with no scope defaults to covering the ENTIRE origin,
// so that cache-first /static/ handling was silently serving the DESKTOP
// app's JS bundle stale too — every deploy looked "live" only after a hard
// refresh. An explicit narrower scope is allowed without any extra server
// header; only widening the scope beyond the script's own directory needs
// Service-Worker-Allowed.
if ('serviceWorker' in navigator && (window.location.protocol === 'https:' || window.location.hostname === 'localhost')) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/m/' }).catch(() => { /* ignore — degrades to plain web */ });
    // One-time cleanup: anyone who already has the old root-scoped worker
    // installed (every existing user, pre-fix) keeps it forever otherwise —
    // a new scoped registration doesn't retroactively remove a broader one.
    navigator.serviceWorker.getRegistrations().then(regs => {
      regs.forEach(reg => {
        if (!new URL(reg.scope).pathname.startsWith('/m/')) reg.unregister();
      });
    }).catch(() => {});
  });
}
